import React, { useState, useRef } from 'react';
import { Card, Button, Row, Col, Alert } from 'react-bootstrap';
import { PostureScanImages } from '../types';
import { IMAGE_VALIDATION, STORAGE_KEYS } from '../config';

interface ImageUploadProps {
  onContinue: (images: PostureScanImages) => void;
  onBack: () => void;
  initialImages?: PostureScanImages | null;
}

interface ImageData {
  file: File | null;
  preview: string | null;
  base64: string | null;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onContinue,
  onBack,
  initialImages,
}) => {
  const [frontImage, setFrontImage] = useState<ImageData>({
    file: null,
    preview: null,
    base64: initialImages?.frontImage || null,
  });

  const [sideImage, setSideImage] = useState<ImageData>({
    file: null,
    preview: null,
    base64: initialImages?.sideImage || null,
  });

  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<'front' | 'side' | null>(null);

  const frontInputRef = useRef<HTMLInputElement>(null);
  const sideInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type - be lenient with MIME type validation
    const allowedTypes = IMAGE_VALIDATION.ALLOWED_TYPES as readonly string[];
    const fileName = file.name.toLowerCase();
    const allowedExtensions = IMAGE_VALIDATION.ALLOWED_EXTENSIONS;
    
    // Check if file has allowed extension or MIME type
    const hasAllowedExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    const hasAllowedMimeType = allowedTypes.includes(file.type);
    
    // Also allow empty MIME type for HEIC files (some browsers don't set it correctly)
    const isLikelyHeic = fileName.endsWith('.heic') || fileName.endsWith('.heif');
    
    if (!hasAllowedExtension && !hasAllowedMimeType && !isLikelyHeic) {
      return `Invalid file type. Only ${IMAGE_VALIDATION.ALLOWED_EXTENSIONS.join(', ')} files are allowed.`;
    }

    // Check file size
    if (file.size > IMAGE_VALIDATION.MAX_SIZE_BYTES) {
      return `File size exceeds ${IMAGE_VALIDATION.MAX_SIZE_MB}MB limit.`;
    }

    return null;
  };

  const convertToBase64 = async (file: File): Promise<{base64: string, convertedFile: File}> => {
    return new Promise(async (resolve, reject) => {
      try {
        let fileToConvert = file;

        // Check if file is HEIC/HEIF format
        if (file.type === 'image/heic' || file.type === 'image/heif' || 
            file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
          
          // Dynamically import heic2any for HEIC conversion
          const heic2any = (await import('heic2any')).default;
          
          // Convert HEIC to JPEG
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9,
          });

          // Handle both single blob and array of blobs
          const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
          
          // Create a new File object from the converted blob
          fileToConvert = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
        }

        // Convert to base64
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip the data-url prefix (e.g., "data:image/jpeg;base64,")
          const base64 = result.split(',')[1];
          resolve({base64, convertedFile: fileToConvert});
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(fileToConvert);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleFileSelect = async (file: File, type: 'front' | 'side') => {
    setError(null);

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      // Convert to base64 (also converts HEIC to JPEG if needed)
      const {base64, convertedFile} = await convertToBase64(file);
      
      // Create preview URL from the converted file (so HEIC previews work)
      const preview = URL.createObjectURL(convertedFile);

      const imageData: ImageData = {
        file: convertedFile,
        preview,
        base64,
      };

      if (type === 'front') {
        // Clean up old preview
        if (frontImage.preview) {
          URL.revokeObjectURL(frontImage.preview);
        }
        setFrontImage(imageData);
      } else {
        // Clean up old preview
        if (sideImage.preview) {
          URL.revokeObjectURL(sideImage.preview);
        }
        setSideImage(imageData);
      }
    } catch (err) {
      console.error('Error processing image:', err);
      const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                     file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      
      if (isHeic) {
        setError('Failed to convert iPhone image. Please try converting to JPG first or use a different photo.');
      } else {
        setError('Failed to process image. Please try again.');
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, type: 'front' | 'side') => {
    e.preventDefault();
    setDragOver(null);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0], type);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, type: 'front' | 'side') => {
    e.preventDefault();
    setDragOver(type);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'front' | 'side') => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0], type);
    }
  };

  const handleRemoveImage = (type: 'front' | 'side') => {
    if (type === 'front') {
      if (frontImage.preview) {
        URL.revokeObjectURL(frontImage.preview);
      }
      setFrontImage({ file: null, preview: null, base64: null });
    } else {
      if (sideImage.preview) {
        URL.revokeObjectURL(sideImage.preview);
      }
      setSideImage({ file: null, preview: null, base64: null });
    }
    setError(null);
  };

  const handleContinue = () => {
    if (!frontImage.base64 || !sideImage.base64) {
      setError('Please upload both front and side images before continuing.');
      return;
    }

    const images: PostureScanImages = {
      frontImage: frontImage.base64,
      sideImage: sideImage.base64,
    };

    // Don't save to storage - images are too large for localStorage
    // Just pass directly to the API
    onContinue(images);
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (frontImage.preview) URL.revokeObjectURL(frontImage.preview);
      if (sideImage.preview) URL.revokeObjectURL(sideImage.preview);
    };
  }, [frontImage.preview, sideImage.preview]);

  const renderUploadBox = (
    type: 'front' | 'side',
    imageData: ImageData,
    inputRef: React.RefObject<HTMLInputElement>
  ) => {
    const isActive = dragOver === type;
    const hasImage = !!imageData.preview || !!imageData.base64;

    return (
      <div
        className={`border rounded p-4 text-center position-relative ${
          isActive ? 'border-primary bg-primary bg-opacity-10' : 'border-2 border-dashed'
        }`}
        onDrop={(e) => handleDrop(e, type)}
        onDragOver={(e) => handleDragOver(e, type)}
        onDragLeave={handleDragLeave}
        style={{ minHeight: '300px', cursor: 'pointer' }}
        onClick={() => !hasImage && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_VALIDATION.ALLOWED_TYPES.join(',')}
          onChange={(e) => handleFileInputChange(e, type)}
          style={{ display: 'none' }}
          aria-label={`Upload ${type} view image`}
        />

        {hasImage ? (
          <div className="position-relative">
            <img
              src={imageData.preview || `data:image/jpeg;base64,${imageData.base64}`}
              alt={`${type} view`}
              className="img-fluid rounded mb-3"
              style={{ maxHeight: '250px', objectFit: 'contain' }}
            />
            <div className="d-flex gap-2 justify-content-center">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveImage(type);
                }}
              >
                Remove
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                Replace
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <i className="bi bi-cloud-upload fs-1 text-primary"></i>
            </div>
            <h5 className="mb-2">Upload {type === 'front' ? 'Front' : 'Side'} View</h5>
            <p className="text-muted mb-3">
              Drag & drop your image here or click to browse
            </p>
            <Button variant="outline-primary" size="sm">
              Choose File
            </Button>
            <div className="mt-3">
              <small className="text-muted">
                Accepted: JPG, PNG • Max {IMAGE_VALIDATION.MAX_SIZE_MB}MB
              </small>
            </div>
          </>
        )}
      </div>
    );
  };

  const canContinue = !!frontImage.base64 && !!sideImage.base64;

  return (
    <Card className="mb-4 border-0 shadow-lg">
      <Card.Header className="bg-primary text-white py-3">
        <h3 className="mb-0" style={{ color: 'white' }}>Upload Posture Images</h3>
      </Card.Header>

      <Card.Body className="p-5">
        <Alert variant="info" className="mb-4">
          <Alert.Heading className="h6">📸 Image Guidelines</Alert.Heading>
          <ul className="mb-0 small">
            <li>Ensure full body is visible in both images</li>
            <li>Stand straight and maintain neutral posture</li>
            <li>Use good lighting with minimal shadows</li>
            <li>Keep background simple and uncluttered</li>
            <li>Front view: Face camera directly, arms at sides</li>
            <li>Side view: Turn 90° to show your profile</li>
          </ul>
        </Alert>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row className="mb-4">
          <Col md={6} className="mb-3 mb-md-0">
            <h5 className="mb-3 text-center">Front View <span className="text-danger">*</span></h5>
            {renderUploadBox('front', frontImage, frontInputRef)}
          </Col>

          <Col md={6}>
            <h5 className="mb-3 text-center">Side View <span className="text-danger">*</span></h5>
            {renderUploadBox('side', sideImage, sideInputRef)}
          </Col>
        </Row>

        <div className="d-flex gap-3 justify-content-between mt-4">
          <Button
            variant="outline-secondary"
            onClick={onBack}
            size="lg"
          >
            ← Back
          </Button>

          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={!canContinue}
            size="lg"
            className="px-5"
          >
            Continue to Analysis →
          </Button>
        </div>

        <div className="mt-4 text-center">
          <small className="text-muted">
            {canContinue 
              ? '✓ Both images uploaded and ready for analysis'
              : 'Please upload both front and side images to continue'}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ImageUpload;
